import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { AnyEntity, Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import EditStageModal from "@app/component/createEditEntity/EditStageModal";
import { StandardFilterButton } from "@app/component/filtering";
import GenericView from "@app/component/view/GenericView/GenericView";
import DealClosedDateSettingValue from "@app/enum/DealClosedDateSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import FunnelSummaryInfo from "@app/scene/deal/type/FunnelSummaryInfo";
import filterOperatorsForKanban from "@app/scene/deal/util/filterOperatorsForKanban";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { fetchDeal, updateDeal } from "@app/store/deal/actions";
import { getOrganizationSettingValue, isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import showClosingDateAlert from "@app/util/deal/showClosingDateAlert";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";
import { formatDate } from "@app/util/formatters";
import { dealLayoutModel } from "@app/util/layout/impl";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import ViewMode from "../../enum/ViewMode";
import {
  getKanbanDeals,
  getKanbanViewFunnelSummary,
  getKanbanViewState,
  getKanbanViewTotalFilteredRecordsCount,
  getKanbanViewTotalRecordsCount,
  isKanbanViewLoading,
} from "../../store";
import {
  fetchKanbanDeals,
  initializeKanbanView,
  updateKanbanByDealChanging,
} from "../../store/actions";
import { StagesWithDeals } from "../../store/types/KanbanView";
import { dealKanbanViewAnalytics } from "../../util/dealTableAnalytics";
import CardSorter from "../CardSorter";
import ManageFunnelsModal from "../ManageFunnelsModal";
import Subheader from "../Subheader";
import Toolbar from "../Toolbar";
import QuickFunnelFilter from "../Toolbar/QuickFunnelFilter";
import QuickOwnerFilter from "../Toolbar/QuickOwnerFilter";
import TotalValues from "../TotalValues";

import KanbanBoard from "./components/KanbanBoard";
import styles from "./KanbanPage.module.scss";
import useSavedFilterProcessor from "./util/useSavedFilterProcessor";

interface Props {
  closedDateUpdateSetting: DealClosedDateSettingValue;
  fetcher: typeof fetchKanbanDeals.request;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  funnelSummary: FunnelSummaryInfo;
  groupedDeals: StagesWithDeals;
  initialize: () => void;
  isMember?: boolean;
  loading?: boolean;
  onFetchDeal: typeof fetchDeal;
  onUpdateDeal: typeof updateDeal.request;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
  updateDealData: typeof updateKanbanByDealChanging.request;
  viewState: ViewState;
}

const KanbanPage: React.FC<Props> = ({
  closedDateUpdateSetting,
  fetcher,
  funnels,
  funnelStages,
  funnelSummary,
  groupedDeals,
  initialize,
  isMember,
  loading,
  onFetchDeal,
  onUpdateDeal,
  subscribe,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
  updateDealData,
  viewState,
}) => {
  const intl = useIntl();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const [dealToEdit, setDealToEdit] = useState<Deal | undefined>();

  const handleDrop = useCallback(
    (stage: Stage, deal: Deal) => {
      // We need this additional fetchDeal call since deals in Kanban board do not
      // have some fields (since include* flags are ignored for aggregated results atm:
      // https://mapmycustomers.atlassian.net/browse/TART-15464 ).
      // But we need at least groups and CFs to check if there are any missing values.
      onFetchDeal({
        id: deal.id,
        callback: (fullDeal: Deal) => {
          const updatedDeal = { ...fullDeal, stage };
          const hasMissingFields = dealLayoutModel.hasMissingRequiredFieldsFor(updatedDeal);

          if (hasMissingFields) {
            setDealToEdit(updatedDeal);
          } else {
            const today = new Date();
            if (
              closedDateUpdateSetting === DealClosedDateSettingValue.ASK_EVERYTIME &&
              stage.type !== DealStageType.DEFAULT &&
              deal.stage.id !== stage.id &&
              deal.closingDate
            ) {
              showClosingDateAlert(
                () => {
                  onUpdateDeal({ ...deal, closingDate: formatDate(today, "yyyy-MM-dd") });
                },
                () => {
                  onUpdateDeal({ ...deal, stage });
                },
                intl
              );
            } else {
              // Not using updatedDeal for a reason: we don't want files in the payload
              onUpdateDeal({ ...deal, stage });
            }
          }
        },
        options: { includeCustomFields: true, includeFiles: true, includeGroups: true },
      });
    },
    [onFetchDeal, closedDateUpdateSetting, intl, onUpdateDeal]
  );

  const handleHideEditStage = useCallback(() => {
    setDealToEdit(undefined);
  }, []);

  const [isFunnelSettingsModalOpen, showFunnelSettings, hideFunnelSettings] = useBoolean();
  const [defaultFunnelId, setDefaultFunnelId] = useState<Funnel["id"] | undefined>();

  const handleOpenFunnelSettings = useCallback(
    (funnelId: Funnel["id"]) => {
      setDefaultFunnelId(funnelId);
      showFunnelSettings();
      dealKanbanViewAnalytics?.clicked(["Open Stage Preferences"]);
    },
    [showFunnelSettings]
  );

  const handleHideFunnelSettings = useCallback(() => {
    setDefaultFunnelId(undefined);
    hideFunnelSettings();
  }, [hideFunnelSettings]);

  const funnelId = useMemo(
    () =>
      viewState.filter.funnel &&
      isSimpleCondition(viewState.filter.funnel) &&
      viewState.filter.funnel.operator === FilterOperator.EQUALS
        ? (viewState.filter.funnel.value as Funnel["id"])
        : undefined,
    [viewState.filter]
  );

  useEffect(() => {
    const listener = {
      onAdd: (entityType: EntityType, entities: AnyEntity[]) => updateDealData(entities as Deal[]),
      onDelete: (entityType: EntityType, entityIds: AnyEntity["id"][]) =>
        updateDealData(
          Object.values(groupedDeals).reduce(
            (result, { deals: data }) => [
              ...result,
              ...data.filter(({ id }) => entityIds.includes(id)),
            ],
            [] as Deal[]
          )
        ),
      onUpdate: (entityType: EntityType, entities: AnyEntity[]) =>
        updateDealData(entities as Deal[]),
    };
    subscribe({ entityTypes: [EntityType.DEAL], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.DEAL], listener });
    };
  }, [groupedDeals, fetcher, subscribe, unsubscribe, updateDealData]);

  const hiddenFilterFields = useMemo(
    () =>
      [
        dealFieldModel.getByName("stage"), // because why showing it if we have stages displayed
        ...dealFieldModel.fields.filter(
          (field) =>
            (field.hasFeature(FieldFeature.NON_LIST_VIEW) &&
              !field.hasAnyFeature(FieldFeature.VIEW_AS_FIELD, FieldFeature.TEAM_FIELD)) ||
            (isMember && field.hasFeature(FieldFeature.VIEW_AS_FIELD))
        ),
      ].filter(isDefined),
    [isMember]
  );

  const hiddenSavedFilterFields = useMemo(
    () => [dealFieldModel.getByName("funnel")].filter(isDefined),
    []
  );

  const savedFilterProcessor = useSavedFilterProcessor(funnels, funnelId);

  return (
    <AnalyticsContext.Provider value={dealKanbanViewAnalytics}>
      <GenericView
        entityType={EntityType.DEAL}
        fetcher={fetcher}
        fieldModel={dealFieldModel}
        viewState={viewState}
      >
        <Subheader totalFilteredRecords={totalFilteredRecords} totalRecords={totalRecords} />
        <div className={styles.container}>
          <Toolbar
            extra={[
              <CardSorter key="cardSorter" />,
              <QuickOwnerFilter key="ownerFilter" />,
              <QuickFunnelFilter key="funnelFilter" />,
            ]}
            filterButton={
              <StandardFilterButton
                context="dealPage"
                entityType={EntityType.DEAL}
                filterOperators={filterOperatorsForKanban}
                hiddenFields={hiddenFilterFields}
                implicitFilterFields={hiddenSavedFilterFields}
                processSavedFilter={savedFilterProcessor}
              />
            }
            viewMode={ViewMode.KANBAN}
          />
          <TotalValues dealsCount={totalFilteredRecords} funnelSummary={funnelSummary} />
          <KanbanBoard
            className={styles.board}
            groupedDeals={groupedDeals}
            loading={loading}
            onDrop={handleDrop}
            onOpenFunnelSettings={handleOpenFunnelSettings}
            stages={funnelId ? funnelStages[funnelId] ?? [] : []}
          />
        </div>

        {isFunnelSettingsModalOpen && (
          <ManageFunnelsModal defaultFunnelId={defaultFunnelId} onHide={handleHideFunnelSettings} />
        )}

        {dealToEdit && (
          <EditStageModal deal={dealToEdit} fixedFunnelStage onHide={handleHideEditStage} />
        )}
      </GenericView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  closedDateUpdateSetting: getOrganizationSettingValue(state)(
    OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE
  ),
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
  funnelSummary: getKanbanViewFunnelSummary(state),
  groupedDeals: getKanbanDeals(state),
  isMember: isCurrentUserMember(state),
  loading: isKanbanViewLoading(state),
  totalFilteredRecords: getKanbanViewTotalFilteredRecordsCount(state),
  totalRecords: getKanbanViewTotalRecordsCount(state),
  viewState: getKanbanViewState(state),
});

const mapDispatchToProps = {
  fetcher: fetchKanbanDeals.request,
  initialize: initializeKanbanView.request,
  onFetchDeal: fetchDeal,
  onUpdateDeal: updateDeal.request,
  subscribe,
  unsubscribe,
  updateDealData: updateKanbanByDealChanging.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(KanbanPage);
